import React, { useState, useMemo } from 'react';
import { graphql } from 'gatsby';
import { Helmet } from 'react-helmet';
import { base, Button, TextInput, Text, Select, Form } from 'grommet';
import { Form as FinalForm, Field } from 'react-final-form';
import styled from '@emotion/styled';
import parse from 'html-react-parser';

import LoginComponent from '../../components/Login';
import { sleep } from '../../utils';
import { calculateVersion } from '../entitynames';

const {
  global: { colors: themeColors },
} = base;

const StyledInput = styled(TextInput)`
  max-width: 30%;
`;

const StyledLabel = styled(Text)`
  display: block;
  margin: 1rem 0 0.2rem 0;
`;

const StyledButton = styled(Button)`
  margin-top: 1rem;
  border-radius: 2em;
  font-size: 0.8rem;
  padding: 1em 2rem;
`;

const Error = styled.span`
  width: 30%;
  display: block;
  color: ${themeColors['status-error']};
  margin: 1rem 0;
`;

const Loading = styled.span`
  display: block;
  margin: 1rem 0;
`;

const MD_CONTENT_SCRAPER = 'https://mdcontentscraper.dart.masterdocs.com.au';

const doScrape = (username, password, target) =>
  fetch(`${MD_CONTENT_SCRAPER}/start`, {
    method: 'POST',
    headers: {
      'Content-type': 'application/json',
    },
    body: JSON.stringify({ username, password, target }),
  }).then(d => d.json());

const getScrapeStatus = id =>
  fetch(`${MD_CONTENT_SCRAPER}/status?id=${id}`).then(d => d.json());

const FormBody = ({ selectOptions, handleSubmit, values }) => {
  return (
    <Form onSubmit={handleSubmit}>
      <Field
        name='entity'
        render={({ input }) => (
          <>
            <StyledLabel>Entity</StyledLabel>
            <Select
              options={selectOptions}
              {...input}
              onChange={({ option }) => input.onChange(option)}
            />
          </>
        )}
      />
      <Field
        name='username'
        render={({ input }) => (
          <>
            <StyledLabel>Masterdocs username</StyledLabel>
            <StyledInput
              type='email'
              {...input}
              onChange={e => input.onChange(e.target.value)}
            />
          </>
        )}
      />
      <Field
        name='password'
        render={({ input }) => (
          <>
            <StyledLabel>Masterdocs password</StyledLabel>
            <StyledInput
              type='password'
              {...input}
              onChange={e => input.onChange(e.target.value)}
            />
          </>
        )}
      />
      <StyledButton primary type='submit' disabled={!values.entity}>
        {values.entity
          ? `Run content scraper on ${values.entity}`
          : 'Select an entity to run scraper'}
      </StyledButton>
    </Form>
  );
};

const ContentScraper = ({ entities }) => {
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const selectOptions = useMemo(() => entities.map(({ acro }) => acro));

  const handleScrapeContent = async values => {
    try {
      const { entity, username, password } = values;

      if (!entity)
        return setError('Please select an instance to run the scraper on');
      if (!username)
        return setError(
          'Please enter a username for the selected masterdocs instance'
        );
      if (!password)
        return setError(
          'Please enter a password for the selected masterdocs instance'
        );

      setError('');
      setResult('');
      setLoading(true);

      const entityObj = entities.find(e => e.acro === entity);

      const { id } = await doScrape(
        username,
        password,
        `https://${entityObj.url}`
      );
      if (id) {
        const { html, status } = await checkScrapeStatusRecursive(id);
        setResult(html);
        if (status !== 'complete') {
          setError('There was an error while scraping instance content');
        }
        setLoading(false);
      }
    } catch (e) {
      console.error(e);
      setError(`Error while scraping: ${e.message}`);
      setLoading(false);
    }
  };

  const checkScrapeStatusRecursive = async id => {
    while (true) {
      const scrape = await getScrapeStatus(id);
      if (scrape.status !== 'pending') {
        return scrape;
      }
      setLoading(scrape.progress);
      await sleep(7000);
      return checkScrapeStatusRecursive(id);
    }
  };

  return (
    <div>
      <Helmet>
        <meta charSet='utf-8' />
        <title>Content scraper - MD Dashboard</title>
      </Helmet>
      <h1>Content scraper</h1>
      <div>
        <FinalForm
          onSubmit={handleScrapeContent}
          render={FormBody}
          selectOptions={selectOptions}
          handleScrapeContent={handleScrapeContent}
        />

        <Error>{error}</Error>
        {loading !== '' && (
          <Loading>
            Loading content from instance.
            <br />
            This may take 15-20 minutes depending on the size of the project.
            <br />
            <br />
            <strong>Current progress:</strong> {loading}
          </Loading>
        )}
      </div>
      <div>{result !== '' ? parse(result) : null}</div>
    </div>
  );
};

export default ({ data }) => {
  const entitydatabase = JSON.parse(
    data.baseline.edges[0].node.childRawCode.content
  );
  const patches = data.patches.edges.map(
    ({
      node: {
        childRawCode: { content },
        base,
      },
    }) => ({
      content: JSON.parse(content),
      name: base
        .split('.')
        .shift()
        .substring(2),
    })
  );
  const [patchVersions, calculatedState] = patches.reduce(
    ([allPrevious], { content }) => [
      allPrevious.concat([content]),
      calculateVersion(entitydatabase, [...allPrevious, content]),
    ],
    [[], []]
  );
  return (
    <LoginComponent>
      {() => <ContentScraper entities={calculatedState} />}
    </LoginComponent>
  );
};

export const query = graphql`
  {
    patches: allFile(filter: { base: { glob: "*.jsonpatch.json" } }) {
      ...filecontent
    }
    baseline: allFile(filter: { base: { eq: "entitydatabase.json" } }) {
      ...filecontent
    }
  }

  fragment filecontent on FileConnection {
    edges {
      node {
        base
        name
        childRawCode {
          content
        }
      }
    }
  }
`;
